import React, { useCallback, useState } from 'react';
import _, { camelCase, startCase } from 'lodash';
import ErrorHandler from '../../error/ErrorHandler';
import ConciseCollection from '../../common/ConciseCollection';
import CollectionUtil from './../../queries/lightbox/CollectionUtil';
import Modal from '../Modal2';
import { ZmCollection } from '../../autogenerated/ZoomBridge';
import HashMap from '../../protocol/HashMap';
import { toast } from '../../store/StoreUtil';
import { getErrorMessage } from '../../util/ErrorUtil';
import t from '../../i18n/I18n';
import { ThemeSetup } from '../../themes/ThemeSetup';
import Button from '../../common/Button';
import Popover from '../../Popover';
import { useOuterClick } from '../../util/CustomHooks';
import { ThemeList } from '../../themes/ThemeList';
import CollectionModalV2 from '../collections/collection-modal-v2/CollectionModalV2';

const debug = require('../../debug/debug')('wc:modals:CollectionsOperationModal');

type Props = {
  collections: ZmCollection[],
  operation: 'HIDE' | 'DELETE',
  onClose: Function,
  onDeleteSuccess: Function,
  onArchiveSuccess: Function,
  onRestoreSuccess: Function
};

export default class CollectionsOperationModal extends React.Component<Props, {}> {
  _collectionMetadataMap: HashMap;

  static defaultProps = {
    onDeleteSuccess: _.noop,
    onArchiveSuccess: _.noop,
    onRestoreSuccess: _.noop
  };

  constructor(props) {
    super(props);
    this._collectionMetadataMap = new HashMap();
    this.state = { collectionMetadataMapLoaded: false };
  }

  async componentDidMount() {
    await this.getCollectionMetadataOfAllCollections();
  }

  getCollectionMetadata = async (collectionId: number) => {
    try {
      const res: HashMap = await CollectionUtil.getMetadata(collectionId, null, true);
      this.onCollectionMetadataReceived(collectionId, res);
    } catch (err) {
      debug('[getCollectionMetadata()] error: ', err);
      window.setError(err.toString());
    }
  };

  onCollectionMetadataReceived = (collectionId: number, collectionMetadata: HashMap) => {
    this._collectionMetadataMap.put(collectionId, collectionMetadata);
    this.setState({ collectionMetadataMapLoaded: true });
  };

  getCollectionMetadataOfAllCollections = async () => {
    try {
      const { collections } = this.props;
      collections.map((collection) => {
        this.getCollectionMetadata(collection.getCollectionId());
      });
    } catch (err) {
      debug('[getCollectionMetadataOfAllCollections()] error: ', err);
      toast(getErrorMessage(err));
    }
  };

  renderConciseView(zmCollection, i) {
    return <ConciseCollection key={i} collection={zmCollection} style={styles.assetDisplay} />;
  }

  render() {
    const { collections, operation } = this.props;
    if (!collections || !this.state.collectionMetadataMapLoaded) return <div />;

    const suffix = collections.length === 1 ? '' : 's';
    const verb = operation.toLowerCase();
    const del_label = verb == 'delete' ? t('REMOVE_MODAL.ASSET_WILL_NOT_REMOVE') : '';
    const label =
      collections.length === 1 ? (
        <span>
          {t('REMOVE_MODAL.REMOVE_TEXT')} {verb} <b>{collections[0].getName()}</b>? {del_label}
        </span>
      ) : (
        `${t('REMOVE_MODAL.REMOVE_TEXT')} ${verb} ${t('REMOVE_MODAL.SUB_STRING_REMOVE_TEXT')} ${del_label}`
      );

    const modalProps = {
      name: 'CollectionsOperationModal',
      title: t('DELETE_LABEL'),
      // autoCloseOnAction: false,
      onClose: () => this.props.onClose(null, false),
      // buttons: [
      //   { label: t('CANCEL'), type: 'CANCEL', onClick: () => this.props.onClose(null, false) },
      //   {
      //     label: operation + ' ' + t('COLLECTION') + suffix.toUpperCase(),
      //     type: 'PRIMARY',
      //     onClick: this.doActionOnCollections
      //   }
      // ]
      onOk: this.doActionOnCollections,
      okBtnText: startCase(camelCase(operation))
    };

    return (
      <CollectionModalV2 {...modalProps}>
        <div style={{ padding: '20px 0' }}>{label}</div>
        {collections.length > 1 ? (
          <div style={{ maxHeight: `${window.innerHeight - 250}px`, overflowY: 'auto', padding: '0px 20px 20px' }}>
            {collections.map(this.renderConciseView)}
          </div>
        ) : null}
      </CollectionModalV2>
    );
  }

  doActionOnCollections = () => {
    const { operation, collections, onClose, reloadCollection } = this.props;

    if (operation === 'DELETE') {
      debug('[doActionOnCollections()] deleting collection: ', collections);
      CollectionUtil.delete(
        collections.map((col) => col.getCollectionId()),
        (success, error) => {
          debug('[doActionOnCollections()] success: ', success);
          if (error) {
            ErrorHandler.handleError(error ? error : t('REMOVE_MODAL.DELETE_FAILED'));
          } else {
            // onDeleteSuccess(collections);
            reloadCollection();
            onClose(collections, true);
            toast(t('DELETED_SUCESSFULLY'))
          }
        }
      );
    } else if (operation === 'HIDE') {
      debug('[doActionOnCollections()] hiding collection: ', collections);
      let collectionIds = [];
      var collectionMetadata = new HashMap();

      for (const collection of collections) {
        const metadataMap = this._collectionMetadataMap.get(collection.getCollectionId());
        const currentStatus = metadataMap.get(CollectionUtil.COLLECTION_STATUS_KEY);
        if (currentStatus !== CollectionUtil.COLLECTION_ARCHIVED_KEY) {
          collectionIds.push(collection.getCollectionId());
          collectionMetadata.put(collection.getCollectionId(), CollectionUtil.getMetadataMapForArchive(currentStatus));
        }
      }

      if (collectionIds.length > 0) {
        CollectionUtil.archiveCollection(collectionIds, collectionMetadata).then(
          (result) => {
            debug('[doActionOnCollections()] success: ', result);
            // onArchiveSuccess(collections);
            reloadCollection();
            onClose(collections, true);
            toast(t('HIDE_SUCCESSFULLY'))
          },
          (errorId) => {
            ErrorHandler.handleError(errorId ? errorId.toString() : t('REMOVE_MODAL.ARCHIVE_FAILED'));
          }
        );
      } else {
        onClose(collections, true);
        toast(t('NO_ELIGIBLE_COLLECTIONS_TO_HIDE'));
      }
    } else if (operation === 'UNHIDE') {
      debug('[doActionOnCollections()] unhiding collection: ', collections);
      let collectionIdList = [];
      var collectionMetadata = new HashMap();

      for (const coll of collections) {
        const metadataMap2 = this._collectionMetadataMap.get(coll.getCollectionId());
        let currentStatus2 = metadataMap2.get(CollectionUtil.COLLECTION_STATUS_KEY);
        if (currentStatus2 === CollectionUtil.COLLECTION_ARCHIVED_KEY) {
          let previousStatus = metadataMap2.get(CollectionUtil.COLLECTION_PREVIOUS_STATUS_KEY);
          if (!previousStatus) {
            previousStatus = CollectionUtil.COLLECTION_NOT_STARTED_KEY;
          }

          collectionIdList.push(coll.getCollectionId());
          collectionMetadata.put(coll.getCollectionId(), CollectionUtil.getMetadataMapForRestore(previousStatus));
        }
      }

      if (collectionIdList.length > 0) {
        CollectionUtil.restoreCollection(collectionIdList, collectionMetadata).then(
          (result) => {
            debug('[doActionOnCollections()] success: ', result);
            // onRestoreSuccess(collections);
            reloadCollection();
            onClose(collections, true);
            toast(t('UNHIDE_SUCCESSFULLY'))
          },
          (errorId) => {
            ErrorHandler.handleError(errorId ? errorId.toString() : t('REMOVE_MODAL.RESTORE_FAILED'));
          }
        );
      } else {
        onClose(collections, true);
        toast(t('NO_ELIGIBLE_COLLECTIONS_TO_UNHIDE'));
      }
    }
  };
}

const styles = {
  assetDisplay: {
    marginTop: '0.5em'
    // background: 'transparent',
    // boxShadow: 'none'
  }
};

export const SaveCollectionModal = (props) => {
  let { collections } = props;
  const [name, setName] = useState(collections[0].getName() || '');
  return (
    <Modal
      closeOnEsc
      closeOnOutsideClick
      name="renameCollectionModal"
      title={props.title}
      buttons={[
        {
          label: t('CANCEL_LABEL'),
          type: 'CANCEL',
          onClick: props.onClose,
          style: { border: '1.5px solid #b5b5b5', borderRadius: '4px', padding: '10px 16px' }
        },
        {
          label: t('RENAME_COLLECTION'),
          type: 'PRIMARY',
          onClick: () => props.save([name, collections[0], props.onClose]),
          style: { padding: '10px 16px' }
        }
      ]}
      footerStyle={{ paddingTop: '20px' }}
      bodyStyle={{ padding: '32px', margin: '0 -20px 0' }}
      onClose={props.onClose}>
      <div>
        <label style={{ lineHeight: '20px', fontSize: '14px', color: 'var(--tree-node-font-color)' }}>
          Enter new name*
        </label>
        <input
          type="text"
          placeholder={t('NAME')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ width: '100%' }}
        />
      </div>
    </Modal>
  );
};

export const SaveCollectionAndFilters = ({ onCollectionSave }) => {
  const [show, setShow] = useState(false);
  const [showModal, setShowModal] = useState('');

  const openPopOver = useCallback(() => {
    const position = getPopoverPosition('SAVE_CURRENT_SEARCH_FILTER');
    setShow(position);
  });

  const popovermodal = useCallback((value) => {
    setShowModal(value);
    setShow(null);
    console.log(value, 'value');
  });

  const clickSave = (name) => {
    showModal === 'SEARCH' ? '' : onCollectionSave(name);
    setShowModal('');
  };

  const innerRef = useOuterClick((e) => {
    setShow(null);
  });

  return (
    <div ref={innerRef}>
      <Button
        title={`${t('SAVE_COLLECTION')}.`}
        onClick={openPopOver}
        id="SAVE_CURRENT_SEARCH_FILTER"
        icon={ThemeSetup.getImageURL('send_to_s3.svg')}></Button>
      {show ? (
        <div
          style={{
            top: show.top,
            left: show.left,
            width: '200px',
            position: 'fixed',
            zIndex: '1',
            background: 'var(--primary-background)'
          }}
          onClose={() => setShow(false)}>
          <div
            style={{
              padding: '10px 10px 3px',
              color: 'var(--primary-font-color-selected)',
              width: '200px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
            onClick={() => popovermodal('SEARCH')}>
            {t('SAVE_CURRENT_SEARCH')}
          </div>
          <div
            style={{
              padding: '10px 10px 3px',
              color: 'var(--primary-font-color-selected)',
              fontSize: '14px',
              cursor: 'pointer'
            }}
            onClick={() => popovermodal('COLLECTION')}>
            {t('SAVE_COLLECTION')}
          </div>
        </div>
      ) : null}
      {showModal && (
        <SaveCollectionModal
          title={showModal === 'SEARCH' ? t('ENTER_FILTER_NAME') : t('ENTER_FILTER_NAME_COLLECTION')}
          onClose={() => setShowModal('')}
          save={clickSave}
        />
      )}
    </div>
  );
};

function getPopoverPosition(id) {
  const element = document.getElementById(id);
  const rect = element.getBoundingClientRect();
  return { top: rect.top + 10, left: rect.left - 200 };
}
